import { IntegrationsAdapter } from '../adapters/integrations-adapter';
import { CallDirection } from '../models/call-direction.models';
import {
  CallAnsweredEventData,
  CallEventTypes,
  CallRangEventData,
  CallStartedEventData,
} from '../models/call-events.models';
import { Integration, LaunchAutoPopType, WebPopCallObject } from '../models/integrations.models';
import { WebpopSettings } from '../models/webpop-settings.models';

/**
 * Branded type for event deduplication keys.
 * This ensures type safety and prevents accidental misuse of plain strings.
 */
export type EventCallIdKey = string & { __brand: 'EventCallIdKey' };

/**
 * Creates a unique key for event deduplication based on event type and call ID.
 */
export function getEventCallIdKey(eventType: CallEventTypes, callId: string): EventCallIdKey {
  return `${eventType}-${callId}` as EventCallIdKey;
}

/**
 * Data required to create a WebPopCallObject.
 */
export interface CreateWebPopCallObjData {
  phoneNumber: string;
  callerId: string;
  callerName: string;
  callerNumber?: string;
  dialedNumber: string | null;
  callId: string;
  callDirection: CallDirection;
  dateTime?: string | null;
}

/**
 * Creates a WebPopCallObject from call event data.
 */
export function createWebPopCallObj(data: CreateWebPopCallObjData): WebPopCallObject {
  return {
    phone_number: data.phoneNumber,
    caller_ID_10_digit: getProcessedNationalNumber(data.callerId),
    caller_ID: data.callerId,
    caller_name: data.callerName ?? '',
    caller_number: data.callerNumber ?? '',
    call_direction: data.callDirection,
    date_time: data.dateTime ?? new Date().toISOString(),
    call_ID: data.callId,
    dialed_number: data.dialedNumber ?? '',
    dialed_number_10_digit: data.dialedNumber ? getProcessedNationalNumber(data.dialedNumber) : '',
  };
}

/**
 * Creates an integration URL by replacing parameter placeholders with call data values.
 */
export function createIntegrationUrl(integration: Integration, callObject: WebPopCallObject): string {
  let integrationUrl = integration.launch_url;
  const integrationParams: Record<string, string> = {
    $CALLER_ID_10_DIGIT: getProcessedNationalNumber(callObject.phone_number),
    $PHONE_NUMBER: callObject.phone_number,
    $CALLER_ID: callObject.caller_ID,
    $CALL_ID: callObject.call_ID,
    $DATE_TIME: callObject.date_time ?? new Date().toISOString(),
    $DIALED_NUMBER_10_DIGIT: callObject.dialed_number ? getProcessedNationalNumber(callObject.dialed_number) : '',
    $DIALED_NUMBER: callObject.dialed_number ?? '',
    $CALL_DIRECTION: callObject.call_direction === CallDirection.incoming ? 'incoming' : 'outgoing',
    $CUSTOMER_ID: callObject.customer_id ?? '',
  };

  for (const [key, value] of Object.entries(integrationParams)) {
    integrationUrl = integrationUrl.replaceAll(key, value);
  }
  return integrationUrl;
}

/**
 * Filters integrations to get only those that should auto-open for a given call direction.
 */
export function getAutoWebPopIntegrations(integrations: Integration[], direction: CallDirection): Integration[] {
  return integrations.filter((integration) => {
    const enabled = integration.enabled;
    const autoPopsInDirection = direction === CallDirection.incoming ? integration.auto_pop : integration.auto_dial;
    return enabled && autoPopsInDirection;
  });
}

/**
 * Filters integrations to get those that should auto-open for incoming calls based on launch scenario.
 */
export function getIncomingAutoWebPopIntegrations(
  integrations: Integration[],
  launchType: LaunchAutoPopType
): Integration[] {
  return integrations.filter((i) => i.enabled && i.auto_pop && i.launch_scenarios.onIncomingCall.event === launchType);
}

/**
 * Filters integrations to get those that should auto-open for outgoing calls.
 */
export function getOutgoingAutoWebPopIntegrations(integrations: Integration[]): Integration[] {
  return integrations.filter((i) => i.enabled && i.auto_dial);
}

/**
 * Determines if a web pop should be opened based on the phone number length and settings.
 * Returns true if the number is long enough to warrant opening a web pop.
 */
export function shouldOpenWebPop(
  callObj: WebPopCallObject,
  settings: WebpopSettings | undefined,
  direction: CallDirection
): boolean {
  const allowedDigits =
    direction === CallDirection.incoming
      ? (settings?.autoPopDigitsInbound ?? 4)
      : (settings?.autoPopDigitsOutbound ?? 4);

  const numberToCheck = direction === CallDirection.incoming ? callObj.caller_number : callObj.dialed_number;

  // If number is undefined/empty or longer than allowed digits, should open
  return !numberToCheck || numberToCheck.length > allowedDigits;
}

/**
 * Processes a phone number to get the last 10 digits (national number format).
 */
export function getProcessedNationalNumber(tel: string): string {
  const normalized = getNationalNumberOrDefault(tel);
  return getLastCharacters(normalized, 10);
}

/**
 * Normalizes a phone number by removing non-digit characters.
 */
export function getNationalNumberOrDefault(tel: string): string {
  return tel.replace(/\D/g, '');
}

/**
 * Gets the last N characters from a string.
 */
function getLastCharacters(str: string, charCount: number): string {
  return str.trim().slice(-charCount);
}

/**
 * Manages a set of handled event call IDs with automatic cleanup when size exceeds limit.
 * Returns true if the event was already handled.
 */
export function didHandleEventCallId(
  handledSet: Set<EventCallIdKey>,
  eventType: CallEventTypes,
  callId: string
): boolean {
  const key = getEventCallIdKey(eventType, callId);
  return handledSet.has(key);
}

/**
 * Adds an event call ID to the handled set with automatic cleanup.
 */
export function addEventCallIdToSet(handledSet: Set<EventCallIdKey>, eventType: CallEventTypes, callId: string): void {
  // Clean up if set gets too large
  if (handledSet.size > 1000) {
    // Keep the 5 most recent in case we get a few calls in quick succession
    const callIds = Array.from(handledSet);
    handledSet.clear();
    callIds.slice(callIds.length - 5).forEach((id) => handledSet.add(id));
  }

  const key = getEventCallIdKey(eventType, callId);
  handledSet.add(key);
}

// ========== Shared WebSocket Event Handlers ==========

/**
 * Context required by the shared WebPop event handlers.
 * Both MainIntegrationsService and RendererIntegrationsService provide their own context.
 */
export interface WebPopEventContext {
  handledEventCallIds: Set<EventCallIdKey>;
  adapter: IntegrationsAdapter;
  getProvision: () => { callerId: string; nsUid: string } | undefined;
  getWebpopSettings: () => WebpopSettings | undefined;
}

/**
 * Handles a CallRang WebSocket event by opening matching incoming auto-pop integrations (ON_RING).
 */
export async function handleCallRangEvent(event: CallRangEventData, ctx: WebPopEventContext): Promise<void> {
  if (
    didHandleEventCallId(ctx.handledEventCallIds, CallEventTypes.CallRang, event.orig_callid) ||
    event.caller_id === ctx.getProvision()?.callerId ||
    event.caller_id === 'Call-To-Talk' ||
    event.from === 'Call-To-Talk' ||
    ctx.adapter.hasOutgoingCompanionCall(event.term_callid) ||
    ctx.adapter.hasOutgoingCompanionCall(event.orig_callid)
  ) {
    return;
  }

  addEventCallIdToSet(ctx.handledEventCallIds, CallEventTypes.CallRang, event.orig_callid);

  const callObj = createWebPopCallObj({
    phoneNumber: event.caller_id,
    callerId: event.caller_id,
    callerName: event.caller_name,
    callerNumber: event.from,
    dialedNumber: event.to,
    callId: event.orig_callid,
    callDirection: CallDirection.incoming,
    dateTime: event.time_start,
  });

  if (!shouldOpenWebPop(callObj, ctx.getWebpopSettings(), CallDirection.incoming)) {
    return;
  }

  const integrationsToOpen = getIncomingAutoWebPopIntegrations(
    ctx.adapter.getIntegrations(),
    LaunchAutoPopType.ON_RING
  );
  await Promise.all(integrationsToOpen.map((i) => ctx.adapter.openWebPopForIntegration(i, callObj)));
}

/**
 * Handles a CallAnswered WebSocket event by opening matching incoming auto-pop integrations (ON_ANSWER).
 */
export async function handleCallAnsweredEvent(event: CallAnsweredEventData, ctx: WebPopEventContext): Promise<void> {
  if (
    event.aor !== ctx.getProvision()?.nsUid ||
    didHandleEventCallId(ctx.handledEventCallIds, CallEventTypes.CallAnswered, event.orig_callid) ||
    event.caller_id === ctx.getProvision()?.callerId ||
    event.caller_id === 'Call-To-Talk' ||
    event.from === 'Call-To-Talk' ||
    ctx.adapter.hasOutgoingCompanionCall(event.term_callid) ||
    ctx.adapter.hasOutgoingCompanionCall(event.orig_callid)
  ) {
    return;
  }

  addEventCallIdToSet(ctx.handledEventCallIds, CallEventTypes.CallAnswered, event.orig_callid);

  const callObj = createWebPopCallObj({
    phoneNumber: event.caller_id,
    callerId: event.caller_id,
    callerName: event.caller_name,
    callerNumber: event.from,
    dialedNumber: event.to,
    callId: event.orig_callid,
    callDirection: CallDirection.incoming,
    dateTime: event.time_start,
  });

  if (!shouldOpenWebPop(callObj, ctx.getWebpopSettings(), CallDirection.incoming)) {
    return;
  }

  const integrationsToOpen = getIncomingAutoWebPopIntegrations(
    ctx.adapter.getIntegrations(),
    LaunchAutoPopType.ON_ANSWER
  );
  await Promise.all(integrationsToOpen.map((i) => ctx.adapter.openWebPopForIntegration(i, callObj)));
}

/**
 * Handles a CallStarted WebSocket event by opening matching outgoing auto-pop integrations.
 */
export async function handleCallStartedEvent(event: CallStartedEventData, ctx: WebPopEventContext): Promise<void> {
  if (
    didHandleEventCallId(ctx.handledEventCallIds, CallEventTypes.CallStarted, event.orig_callid) ||
    event.caller_id === 'Call-To-Talk' ||
    event.from === 'Call-To-Talk' ||
    ctx.adapter.hasOutgoingCompanionCall(event.term_callid)
  ) {
    return;
  }

  addEventCallIdToSet(ctx.handledEventCallIds, CallEventTypes.CallStarted, event.orig_callid);

  const callObj = createWebPopCallObj({
    phoneNumber: event.to,
    callerId: event.caller_id,
    callerName: event.caller_name,
    callerNumber: event.from,
    dialedNumber: event.to,
    callId: event.orig_callid,
    callDirection: CallDirection.outgoing,
    dateTime: event.time_start,
  });

  if (!shouldOpenWebPop(callObj, ctx.getWebpopSettings(), CallDirection.outgoing)) {
    return;
  }

  const integrationsToOpen = getOutgoingAutoWebPopIntegrations(ctx.adapter.getIntegrations());
  await Promise.all(integrationsToOpen.map((i) => ctx.adapter.openWebPopForIntegration(i, callObj)));
}
